import { useTranslate } from '@tolgee/react';
import { Button, Drawer, DrawerProps, Tabs } from 'antd';
import ScrollTable from 'components/ScrollTable';
import { reverse, sortBy, uniq } from 'lodash-es';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import toLowerCase from 'utils/toLowerCase';
import { initEcharts, echarts } from 'lib/echarts';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';
import { useQuery } from '@tanstack/react-query';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

interface Data {
  region: string;
  count: number;
  percent: number;
}

interface RegionData extends Data {
  region: General.UpperCaseRegion;
  stateList: Data[];
}

function RegionDrawer({
  isAdvantage,
  problem: { content, cnContent, regionData },
  ...otherProps
}: DrawerProps & {
  isAdvantage?: boolean;
  problem: ReviewAnalysis.Problem;
}) {
  const { t } = useTranslate();

  const { regionDataMap, regionDataList } = useMemo(() => {
    const regionDataMap = regionData.reduce<Partial<Record<General.UpperCaseRegion, RegionData>>>(
      (res, { receiveCountryCode, receiveStateCode, count, percent }) => {
        const percentNum = Number(percent);
        const target = res[receiveCountryCode];
        if (target) {
          target.count += count;
          target.percent += percentNum;
          target.stateList.push({
            region: receiveStateCode,
            count,
            percent: percentNum,
          });
        } else {
          res[receiveCountryCode] = {
            region: receiveCountryCode,
            count,
            percent: percentNum,
            stateList: [
              {
                region: receiveStateCode,
                count,
                percent: percentNum,
              },
            ],
          };
        }
        return res;
      },
      {},
    );
    const regionDataList = reverse(sortBy(Object.values(regionDataMap), 'percent'));
    return {
      regionDataMap,
      regionDataList,
    };
  }, [regionData]);

  const [activeRegion, setActiveRegion] = useState<General.UpperCaseRegion>(
    regionDataList[0]?.region,
  );

  const activeRegionData = activeRegion ? regionDataMap[activeRegion]?.stateList : undefined;

  const { data: activeRegionGeo } = useQuery({
    queryKey: ['isoWithRegion', activeRegion],
    queryFn: ({ queryKey }) => {
      const [, region] = queryKey;
      if (!region) {
        return Promise.resolve({});
      }
      return fetch(
        `https://clients.zhisuitech.com/fett-public/geo/region/${toLowerCase(region)}.json`,
      ).then((res) => res.json());
    },
  });

  const geo = activeRegionGeo?.geo;
  const isoMap = activeRegionGeo?.isoMap;

  const tabs = (
    <Tabs activeKey={activeRegion} onChange={setActiveRegion as any}>
      {regionDataList.map(({ region, count }, index) => {
        return (
          <Tabs.TabPane
            key={region}
            tab={
              <div>
                {index < 3 && (
                  <span
                    style={{
                      background: ['#FF4747', '#FF8F47', '#FFD147'][index],
                    }}
                  >
                    {index + 1}
                  </span>
                )}
                {`${t(`region.${toLowerCase(region)}`, region)} (${count})`}
              </div>
            }
          />
        );
      })}
    </Tabs>
  );

  const table = (
    <ScrollTable
      size="small"
      rowKey="region"
      dataSource={activeRegionData || regionDataList}
      columns={[
        {
          key: 'index',
          title: t('排名'),
          render: (_, __, index) => index + 1,
        },
        {
          key: 'region',
          dataIndex: 'region',
          title: t('地区'),
          render: (region: string) =>
            activeRegionData
              ? (isoMap || {})[region] || region
              : t(`region.${toLowerCase(region)}`, region),
        },
        {
          key: 'count',
          dataIndex: 'count',
          title: isAdvantage ? t('好评数') : t('差评数'),
        },
        {
          key: 'percent',
          dataIndex: 'percent',
          title: t('占比'),
          render: (percent) => formatNumber(percent / 100, '0.00%'),
        },
      ]}
      pagination={false}
    />
  );

  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();

  useLayoutEffect(() => {
    if (!chartDomRef.current || !activeRegion || !activeRegionData || !geo || !isoMap) {
      return;
    }

    echarts.registerMap(activeRegion, geo);

    const chart = initEcharts(chartDomRef.current);

    chartRef.current = chart;

    const counts = uniq(activeRegionData.map((_) => _.count));

    chart.setOption({
      tooltip: {
        show: true,
        trigger: 'item',
        formatter: ({ name, value }: any) => {
          return `${name}<br />${value || '-'}`;
        },
      },
      grid: {
        width: '100%',
        height: '100%',
      },
      visualMap: {
        text: ['High', 'Low'],
        min: counts.length > 1 ? counts[counts.length - 1] : 0,
        max: counts[0],
        inRange: {
          color: ['lightskyblue', 'yellow', 'orangered'],
        },
      },
      series: [
        {
          type: 'map',
          map: activeRegion,
          roam: true,
          itemStyle: {
            areaColor: '#F6F7FB',
            borderColor: '#d0d0d0',
          },
          data: activeRegionData.map(({ region, count }) => {
            return {
              name: isoMap[region] || region,
              value: count,
              label: {
                show: true,
              },
            };
          }),
        },
      ],
    });

    chart.dispatchAction({
      type: 'geoRoam',
      zoom: 1.2,
      originX: (chartDomRef.current.offsetWidth || 0) / 2,
      originY: (chartDomRef.current.offsetHeight || 0) / 2,
    });

    const resizeHandler = () => chart.resize();

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      chart.dispose();
      chartRef.current = undefined;
    };
  }, [activeRegion, activeRegionData, geo, isoMap]);

  return (
    <Drawer
      width={1200}
      title={`${isAdvantage ? t('优势地区分布明细') : t('差评地区分布明细')} - ${
        cnContent || content
      }`}
      {...otherProps}
    >
      <Container>
        <section>
          <div ref={chartDomRef} />
          <span>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                chartRef.current?.dispatchAction({
                  type: 'geoRoam',
                  zoom: 1.5,
                  originX: (chartDomRef.current?.offsetWidth || 0) / 2,
                  originY: (chartDomRef.current?.offsetHeight || 0) / 2,
                });
              }}
            />
            <Button
              size="small"
              icon={<MinusOutlined />}
              onClick={() => {
                chartRef.current?.dispatchAction({
                  type: 'geoRoam',
                  zoom: 0.7,
                  originX: (chartDomRef.current?.offsetWidth || 0) / 2,
                  originY: (chartDomRef.current?.offsetHeight || 0) / 2,
                });
              }}
            />
          </span>
        </section>
        <div>
          {tabs}
          <section>{table}</section>
        </div>
      </Container>
    </Drawer>
  );
}

const Container = styled.div`
  display: flex;
  height: calc(100% + 48px);
  margin: -24px;
  padding: 24px;
  background: #f7f9fa;
  > section {
    flex: 1 0 0;
    position: relative;
    height: 100%;
    > div {
      width: 100%;
      height: 100%;
    }
    > span {
      display: flex;
      flex-direction: column;
      position: absolute;
      left: 6px;
      bottom: 220px;
    }
  }
  > div {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    width: 450px;
    height: 100%;
    background: #fff;
    box-shadow: 0 2px 20px 0 #a7b2cc3d;
    > .ant-tabs {
      .ant-tabs-nav {
        padding: 0 12px;
      }
      .ant-tabs-tab-btn > div {
        display: flex;
        align-items: center;
        > span {
          display: inline-block;
          margin-right: 4px;
          width: 14px;
          height: 14px;
          line-height: 14px;
          text-align: center;
          font-size: 10px;
          color: #fff;
          border-radius: 7px 7px 7px 0;
        }
      }
    }
    > section {
      flex: 1 0 0;
      padding: 12px;
      margin-top: -16px;
      overflow: hidden;
    }
  }
`;

export default RegionDrawer;
